import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, RefreshCw, Plus, Server } from 'lucide-react';
import { toast } from 'sonner';
import ApiService from '@/services/api.service';

interface PoolStatus {
    PROVISIONING: number;
    AVAILABLE: number;
    CLAIMED: number;
    ERROR: number;
    environment: string;
}

interface PoolAccount {
    _id: string;
    account_name: string;
    account_locator: string | null;
    account_locator_url: string | null;
    status: string;
    environment: string;
    created_at: string | null;
    ready_at: string | null;
    error: string | null;
}

interface PoolResponse {
    status: PoolStatus;
    accounts: PoolAccount[];
}

const statusBadgeVariant = (status: string) => {
    switch (status) {
        case 'AVAILABLE': return 'success';
        case 'PROVISIONING': return 'warning';
        case 'CLAIMED': return 'info';
        case 'ERROR': return 'destructive';
        default: return 'secondary';
    }
};

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
};

export default function SnowflakePool() {
    const queryClient = useQueryClient();
    const [replenishing, setReplenishing] = useState(false);
    const [provisioning, setProvisioning] = useState(false);

    const { data, isLoading, isError, refetch } = useQuery<PoolResponse>({
        queryKey: ['snowflake-pool-status'],
        queryFn: () => ApiService.getInstance().request('GET', '/system/snowflake-pool/status') as Promise<PoolResponse>,
        refetchInterval: 15000,
    });

    const handleReplenish = async () => {
        setReplenishing(true);
        try {
            const result = await ApiService.getInstance().request('POST', '/system/snowflake-pool/replenish') as any;
            if (result.jobs_queued > 0) {
                toast.success(`Queued ${result.jobs_queued} provisioning job(s)`);
            } else {
                toast.info('Pool is already at desired size');
            }
            queryClient.invalidateQueries({ queryKey: ['snowflake-pool-status'] });
        } catch {
            toast.error('Failed to replenish pool');
        } finally {
            setReplenishing(false);
        }
    };

    const handleProvision = async () => {
        setProvisioning(true);
        try {
            await ApiService.getInstance().request('POST', '/system/snowflake-pool/provision');
            toast.success('Provisioning job queued');
            queryClient.invalidateQueries({ queryKey: ['snowflake-pool-status'] });
        } catch {
            toast.error('Failed to queue provisioning job');
        } finally {
            setProvisioning(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-8">
                <p className="text-destructive mb-4">Failed to load pool status. You may not have superuser access.</p>
                <Button variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
        );
    }

    const status = data?.status;
    const accounts = data?.accounts || [];

    return (
        <div className="p-8 max-w-4xl">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Server className="h-6 w-6" />
                    <div>
                        <h1 className="text-2xl font-semibold">Snowflake Pool</h1>
                        <p className="text-sm text-muted-foreground">
                            Pre-provisioned Snowflake accounts &middot; {status?.environment}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleProvision} disabled={provisioning}>
                        {provisioning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                        Provision One
                    </Button>
                    <Button size="sm" onClick={handleReplenish} disabled={replenishing}>
                        {replenishing && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                        Replenish Pool
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Available</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{status?.AVAILABLE ?? 0}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Provisioning</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{status?.PROVISIONING ?? 0}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Claimed</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{status?.CLAIMED ?? 0}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Errors</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{status?.ERROR ?? 0}</p>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Pool Accounts</CardTitle>
                </CardHeader>
                <CardContent>
                    {accounts.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                            No available or provisioning accounts. Click Replenish Pool to create some.
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Account Name</TableHead>
                                    <TableHead>Locator</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Ready</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.map((account) => (
                                    <TableRow key={account._id}>
                                        <TableCell className="font-mono text-sm">{account.account_name}</TableCell>
                                        <TableCell className="font-mono text-sm">{account.account_locator ?? '—'}</TableCell>
                                        <TableCell>
                                            <Badge variant={statusBadgeVariant(account.status) as any}>
                                                {account.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{formatDate(account.created_at)}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{formatDate(account.ready_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
